import React from 'react';

const Section = ({ title, children, icon, className = '' }) => (
  <div className="mb-6">
    <h3 className="text-lg font-semibold text-gray-700 border-b-2 border-gray-200 pb-2 mb-3 flex items-center">
      <i className={`fas ${icon} mr-3 text-blue-500`}></i>
      {title}
    </h3>
    <div className="space-y-4">{children}</div>
  </div>
);

const ExperienceItem = ({ item, isLast }) => (
  <div>
    <h4 className="font-bold text-gray-800">{item.title}</h4>
    <p className="text-sm font-semibold text-gray-600">{item.company}</p>
    <p className="text-xs text-gray-500">
      {item.startDate ? new Date(item.startDate).toLocaleDateString('es-ES', { year: 'numeric', month: 'long' }) : ''} - {item.endDate ? new Date(item.endDate).toLocaleDateString('es-ES', { year: 'numeric', month: 'long' }) : 'Presente'}
    </p>
    <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{item.description}</p>
  </div>
);

const EducationItem = ({ item, isLast }) => (
  <div>
    <h4 className="font-bold text-gray-800">{item.degree}</h4>
    <p className="text-sm font-semibold text-gray-600">{item.institution}</p>
    <p className="text-xs text-gray-500">
      {item.startDate ? new Date(item.startDate).toLocaleDateString('es-ES', { year: 'numeric', month: 'long' }) : ''} - {item.endDate ? new Date(item.endDate).toLocaleDateString('es-ES', { year: 'numeric', month: 'long' }) : 'Presente'}
    </p>
  </div>
);

const ProfessionalProfilePreviewModal = ({ modalData }) => {
  const { profile } = modalData;

  if (!profile) return null;

  // Asumimos que la información del usuario está en `profile.userId`
  const user = profile.userId || {};

  return (
    <div className="bg-gray-50 rounded-lg shadow-xl w-full max-w-4xl mt-10 h-[85vh] flex flex-col">
      {/* Encabezado del Modal */}
      <div className="p-4 border-b bg-white rounded-t-lg flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-800">Vista Previa del Perfil</h2>
        <p className="text-sm text-gray-500">Así es como las empresas verán tu perfil.</p>
      </div>

      {/* Contenido Principal con Scroll */}
      <div className="flex-grow p-6 overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Columna Izquierda (Información Personal) */}
          <div className="md:col-span-1">
            <div className="bg-white p-6 rounded-lg shadow-md text-center sticky top-0">
              <img src={user.avatar} alt={user.username} className="w-24 h-24 rounded-full object-cover mx-auto mb-4 border-4 border-blue-200" />
              <h2 className="text-xl font-bold">{user.firstName} {user.lastName}</h2>
              <p className="text-sm text-gray-500">@{user.username}</p>
              <h1 className="text-md font-semibold text-blue-700 mt-2">{profile.headline || 'Titular no definido'}</h1>
              {profile.cvUrl && (
                <a
                  href={profile.cvUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-4 bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <i className="fas fa-download mr-2"></i>Descargar CV
                </a>
              )}
              {/* 🟢 NUEVO: Widget de Contacto Adicional */}
              <div className="mt-6 pt-4 border-t text-left text-xs space-y-3">
                <h4 className="font-bold text-gray-600 text-center mb-2">Información de Contacto</h4>
                {user.email && (
                  <p className="text-gray-600"><i className="fas fa-envelope w-5 mr-2 text-gray-400"></i>{user.email}</p>
                )}
                {user.phone && (
                  <p className="text-gray-600"><i className="fas fa-phone w-5 mr-2 text-gray-400"></i>{user.phone}</p>
                )}
                {user.website && (
                  <p className="text-gray-600 truncate">
                    <i className="fas fa-globe w-5 mr-2 text-gray-400"></i>
                    <a href={user.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{user.website}</a>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Columna Derecha (Detalles del Perfil) */}
          <div className="md:col-span-2 space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <Section title="Resumen Profesional" icon="fa-user-tie">
                <p className="text-gray-700 whitespace-pre-wrap">{profile.summary || 'Sin resumen profesional.'}</p>
              </Section>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <Section title="Experiencia Laboral" icon="fa-briefcase">
                {profile.experience?.length > 0 ? (
                  profile.experience.map((item, index) => <ExperienceItem key={index} item={item} />)
                ) : (<p className="text-gray-500">No se ha añadido experiencia laboral.</p>)}
              </Section>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <Section title="Educación" icon="fa-graduation-cap">
                {profile.education?.length > 0 ? (
                  profile.education.map((item, index) => <EducationItem key={index} item={item} />)
                ) : (<p className="text-gray-500">No se ha añadido información sobre educación.</p>)}
              </Section>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <Section title="Habilidades" icon="fa-cogs">
                {profile.skills?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {profile.skills.map((skill, index) => (<span key={index} className="bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full">{skill}</span>))}
                  </div>
                ) : (<p className="text-gray-500">No se han añadido habilidades.</p>)}
              </Section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalProfilePreviewModal;